import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, Loader2, RefreshCw, X,
  Star, ShoppingBag, Camera, Search,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { api } from '../api/client';
import { ConfirmModal } from './ConfirmModal';
import { describePromoPrice, getActivePromo } from '../utils/promoPrice';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProductImage {
  id: string;
  url: string;
  altText: string | null;
  sortOrder: number;
  isPrimary: boolean;
}

interface ProductSize {
  id: string;
  size: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  originalPrice: number | null;
  categoryId: string | null;
  gender: 'hombre' | 'mujer' | 'unisex';
  brand: string | null;
  material: string | null;
  color: string | null;
  badge: string | null;
  isActive: boolean;
  isFeatured: boolean;
  category: Category | null;
  images: ProductImage[];
  sizes: ProductSize[];
  promotions?: Array<{ type: string; label: string; buyQty: number | null; payQty: number | null; discountValue: number | null; isActive: boolean; startsAt: number | null; endsAt: number | null }>;
}

interface Filters {
  search: string;
  category: string;
  gender: string;
  featured: boolean;
}

const EMPTY_FILTERS: Filters = { search: '', category: '', gender: '', featured: false };

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const fmt = (n: number) => n.toLocaleString('es-ES', { maximumFractionDigits: 0 });

const GENDER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'hombre', label: 'Hombre' },
  { value: 'mujer', label: 'Mujer' },
  { value: 'unisex', label: 'Unisex' },
] as const;

function getPageRange(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | null)[] = [1];
  if (current > 3) pages.push(null);
  const lo = Math.max(2, current - 1);
  const hi = Math.min(total - 1, current + 1);
  for (let i = lo; i <= hi; i++) pages.push(i);
  if (current < total - 2) pages.push(null);
  pages.push(total);
  return pages;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ProductManager: React.FC = () => {
  const navigate = useNavigate();

  // List
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // Categories for filter dropdown
  const [categoryList, setCategoryList] = useState<Category[]>([]);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productIdToDelete, setProductIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchProducts = useCallback(async (page: number, f: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE), status: 'all' });
      if (f.search.trim()) params.set('search', f.search.trim());
      if (f.category) params.set('category', f.category);
      if (f.gender) params.set('gender', f.gender);
      if (f.featured) params.set('featured', 'true');
      const res = await api.get<{ items: Product[]; total: number }>(`/api/v1/products?${params}`);
      setProducts(res.data.items);
      setTotalProducts(res.data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(1, EMPTY_FILTERS);
  }, [fetchProducts]);

  useEffect(() => {
    api.get<Category[]>('/api/v1/categories')
      .then(res => setCategoryList(res.data))
      .catch(() => {});
  }, []);

  // ── Search & filters ───────────────────────────────────────────────────────

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    fetchProducts(1, filters);
  };

  const handleClearSearch = () => {
    const next = { ...filters, search: '' };
    setFilters(next);
    setCurrentPage(1);
    fetchProducts(1, next);
  };

  const handleFilterChange = (patch: Partial<Filters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setCurrentPage(1);
    fetchProducts(1, next);
  };

  const clearDropdownFilters = () => {
    const next = { ...filters, category: '', gender: '', featured: false };
    setFilters(next);
    setCurrentPage(1);
    fetchProducts(1, next);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    fetchProducts(page, filters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);
  const activeDropdownFilterCount = [filters.category, filters.gender, filters.featured].filter(Boolean).length;
  const hasAnyFilter = !!(filters.search.trim() || filters.category || filters.gender || filters.featured);

  // ── Delete product ─────────────────────────────────────────────────────────

  const openDeleteConfirm = (id: string) => {
    setProductIdToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productIdToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/products/${productIdToDelete}`);
      setDeleteConfirmOpen(false);
      setProductIdToDelete(null);
      const targetPage = products.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(targetPage);
      await fetchProducts(targetPage, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar el producto');
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const primaryImage = (p: Product) =>
    p.images.find(i => i.isPrimary) ?? p.images[0] ?? null;

  // Precio que paga el cliente (con la promo vigente) + precio tachado / leyenda
  const listPrice = (p: Product) => {
    const d = describePromoPrice(p.price, getActivePromo(p.promotions));
    return d.originalPrice ? d : { ...d, originalPrice: p.originalPrice };
  };


  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between md:items-center bg-page-surface px-5 py-3 rounded-2xl border border-page-border gap-4 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto">
          <div>
            <h2 className="font-heading font-bold text-lg text-ink leading-tight flex items-center gap-2">
              <span>Productos</span>
              <span className="text-xs text-ink-faint font-semibold bg-white border border-page-border px-2 py-0.5 rounded-full">
                {totalProducts}
              </span>
            </h2>
          </div>

          {/* Buscador */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={filters.search}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
                className="w-full bg-white border border-page-border rounded-xl pl-9 pr-8 py-1.5 text-xs text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors"
              />
              {filters.search && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={handleSearchSubmit}
              disabled={loading}
              className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-hover disabled:opacity-50 text-white font-bold px-3 py-1.5 rounded-xl text-xs transition-all whitespace-nowrap"
            >
              <Search className="w-3.5 h-3.5" />
              Buscar
            </button>
          </div>
        </div>

        <div className="flex gap-2.5 justify-end w-full md:w-auto flex-shrink-0">
          <button
            onClick={() => fetchProducts(currentPage, filters)}
            disabled={loading}
            className="p-2 bg-white text-ink-muted rounded-xl hover:bg-page-surface transition-all border border-page-border disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => navigate('/products/new')}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-hover text-white font-bold px-4 py-2 rounded-xl shadow-sm shadow-brand-glow transition-all duration-200 text-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nuevo Producto</span>
          </button>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="flex flex-wrap items-center gap-2 bg-page-surface border border-page-border rounded-2xl px-4 py-3 shadow-sm">

        {/* Categoría */}
        <select
          value={filters.category}
          onChange={e => handleFilterChange({ category: e.target.value })}
          className="text-xs border border-page-border bg-white rounded-xl px-3 py-1.5 text-ink focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors cursor-pointer"
        >
          <option value="">Todas las categorías</option>
          {categoryList.map(cat => (
            <option key={cat.id} value={cat.slug}>{cat.name}</option>
          ))}
        </select>

        {/* Género pills */}
        <div className="flex gap-1">
          {GENDER_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handleFilterChange({ gender: value })}
              className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-semibold transition-all ${
                filters.gender === value
                  ? 'bg-brand-primary text-white border-brand-primary shadow-sm shadow-brand-glow'
                  : 'bg-white text-ink-muted border-page-border hover:border-brand-border hover:text-brand-primary hover:bg-brand-surface'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Destacados toggle */}
        <button
          onClick={() => handleFilterChange({ featured: !filters.featured })}
          className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border font-semibold transition-all ${
            filters.featured
              ? 'bg-brand-primary text-white border-brand-primary shadow-sm shadow-brand-glow'
              : 'bg-white text-ink-muted border-page-border hover:border-brand-border hover:text-brand-primary hover:bg-brand-surface'
          }`}
        >
          <Star className={`w-3 h-3 ${filters.featured ? 'fill-current' : ''}`} />
          Destacados
        </button>

        {/* Limpiar filtros */}
        {activeDropdownFilterCount > 0 && (
          <button
            onClick={clearDropdownFilters}
            className="ml-auto flex items-center gap-1 text-[11px] text-ink-faint hover:text-rose-500 transition-colors"
          >
            <X className="w-3 h-3" />
            Limpiar filtros
            <span className="bg-brand-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
              {activeDropdownFilterCount}
            </span>
          </button>
        )}
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3.5 rounded-xl text-xs flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="flex flex-col justify-center items-center py-20 bg-page-surface rounded-2xl border border-page-border">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <p className="text-ink-muted text-sm mt-3">Cargando productos...</p>
        </div>
      )}

      {!loading && totalProducts === 0 && !hasAnyFilter && (
        <div className="text-center py-16 text-ink-faint bg-page-surface rounded-2xl border border-page-border">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-ink-faint" />
          <p className="text-sm">No hay productos. Creá el primero.</p>
        </div>
      )}

      {!loading && totalProducts === 0 && hasAnyFilter && (
        <div className="text-center py-16 text-ink-faint bg-page-surface rounded-2xl border border-page-border">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-ink-faint animate-pulse" />
          <p className="text-sm mb-3">No se encontraron productos con los filtros aplicados.</p>
          <button
            onClick={() => { setFilters(EMPTY_FILTERS); setCurrentPage(1); fetchProducts(1, EMPTY_FILTERS); }}
            className="text-xs text-brand-primary hover:underline"
          >
            Limpiar todos los filtros
          </button>
        </div>
      )}

      {!loading && products.length > 0 && (
        <>
          {/* ── Mobile cards ── */}
          <div className="md:hidden space-y-2.5">
            {products.map((p) => {
              const img = primaryImage(p);
              return (
                <div
                  key={p.id}
                  className={`rounded-2xl p-3 shadow-sm border transition-colors ${
                    p.isActive
                      ? 'bg-white border-emerald-300'
                      : 'bg-white border-page-border'
                  }`}
                >
                  {/* Top row: image + header info + actions */}
                  <div className="flex gap-3">
                    <div className="w-16 h-16 rounded-xl overflow-hidden border border-page-border bg-page-surface flex-shrink-0">
                      {img
                        ? <img src={img.url} alt={p.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-ink-faint text-[9px]">Sin img</div>
                      }
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name + star + actions */}
                      <div className="flex items-start justify-between gap-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <h4 className="font-semibold text-ink text-sm truncate">{p.name}</h4>
                          {p.isFeatured && <Star className="w-3 h-3 text-brand-primary flex-shrink-0" fill="currentColor" />}
                        </div>
                      </div>

                      {/* SKU */}
                      <p className="text-[10px] text-ink-faint mt-0.5">SKU: {p.sku}</p>

                      {/* Meta pills: categoría + género + estado */}
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {p.category && (
                          <span className="text-[10px] font-medium bg-white/80 border border-page-border text-ink-muted px-1.5 py-0.5 rounded-md">
                            {p.category.name}
                          </span>
                        )}
                        <span className="text-[10px] font-medium bg-white/80 border border-page-border text-ink-muted px-1.5 py-0.5 rounded-md capitalize">
                          {p.gender}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md border ${p.isActive
                          ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                          : 'bg-rose-50 border-rose-200 text-rose-500'
                        }`}>
                          {p.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Price + badge */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="font-bold text-ink text-sm">${fmt(listPrice(p).price)}</span>
                    {listPrice(p).originalPrice && (
                      <span className="text-xs text-ink-faint line-through">${fmt(listPrice(p).originalPrice!)}</span>
                    )}
                    {p.badge && (
                      <span className="text-[9px] bg-brand-surface border border-brand-border text-brand-primary px-1.5 py-0.5 rounded font-semibold">
                        {p.badge}
                      </span>
                    )}
                    {p.images.length > 0 && (
                      <span className="ml-auto text-[10px] text-ink-muted font-medium bg-white/80 border border-page-border px-1.5 py-0.5 rounded-md flex-shrink-0">
                        {p.images.length} {p.images.length === 1 ? 'foto' : 'fotos'}
                      </span>
                    )}
                  </div>

                  {listPrice(p).caption && (
                    <p className="text-[10px] text-emerald-600 font-semibold mt-1 leading-tight">{listPrice(p).caption}</p>
                  )}

                  {/* Talles — una sola fila con scroll horizontal */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-[10px] text-ink-faint font-medium flex-shrink-0">Talles:</span>
                    {p.sizes.length > 0 ? (
                      <div className="flex gap-1 overflow-x-auto scrollbar-none">
                        {p.sizes.map(s => (
                          <span key={s.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-page-border text-ink-muted flex-shrink-0">
                            {s.size}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[10px] text-ink-faint">Sin talles</span>
                    )}
                  </div>

                  {/* Sin imágenes: acción para cargarlas */}
                  {p.images.length === 0 && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-page-border/50">
                      <span className="text-[10px] text-ink-muted font-medium bg-white/80 border border-page-border px-1.5 py-0.5 rounded-md flex-shrink-0">
                        0 fotos
                      </span>
                      <button
                        onClick={() => navigate(`/products/${p.id}/edit?focus=images`)}
                        className="flex-1 py-1.5 bg-brand-surface hover:bg-brand-primary border border-brand-border text-brand-primary hover:text-white rounded-xl transition-all flex items-center justify-center gap-1.5 text-xs font-bold shadow-sm"
                      >
                        <Camera className="w-3.5 h-3.5" />
                        <span>Cargar imagen</span>
                      </button>
                    </div>
                  )}

                  {/* Acciones */}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      onClick={() => navigate(`/products/${p.id}/edit`)}
                      className="py-1.5 bg-sky-50 hover:bg-sky-600 border border-sky-200 hover:border-sky-600 text-sky-700 hover:text-white rounded-xl transition-colors flex items-center justify-center gap-1.5 text-xs font-semibold"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Editar</span>
                    </button>
                    <button
                      onClick={() => openDeleteConfirm(p.id)}
                      className="py-1.5 bg-rose-50 hover:bg-rose-600 border border-rose-200 hover:border-rose-600 text-rose-600 hover:text-white rounded-xl transition-colors flex items-center justify-center gap-1.5 text-xs font-semibold"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Eliminar</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Desktop table ── */}
          <div className="hidden md:block overflow-x-auto bg-white rounded-2xl border border-page-border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-page-border text-ink-muted text-xs font-semibold uppercase tracking-wider bg-page-surface">
                  <th className="px-6 py-4">Producto</th>
                  <th className="px-6 py-4">Categoría</th>
                  <th className="px-6 py-4">Precio</th>
                  <th className="px-6 py-4">Talles</th>
                  <th className="px-6 py-4">Fotos</th>
                  <th className="px-6 py-4">Estado</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-page-border text-sm">
                {products.map((p) => {
                  const img = primaryImage(p);
                  return (
                    <tr key={p.id} className="hover:bg-page-surface transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl overflow-hidden border border-page-border bg-page-surface flex-shrink-0">
                            {img
                              ? <img src={img.url} alt={p.name} className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-ink-faint text-[10px]">Sin img</div>
                            }
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-ink truncate max-w-[200px]">{p.name}</h4>
                              {p.isFeatured && <Star className="w-3 h-3 text-brand-primary flex-shrink-0" fill="currentColor" />}
                            </div>
                            <p className="text-[10px] text-ink-muted mt-0.5">SKU: {p.sku}</p>
                            {p.badge && (
                              <span className="text-[10px] bg-brand-surface border border-brand-border text-brand-primary px-1.5 py-0.5 rounded font-semibold mt-1 inline-block">
                                {p.badge}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-ink-muted">{p.category?.name ?? '—'}</div>
                        <div className="text-[10px] text-ink-faint capitalize mt-0.5">{p.gender}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-ink">${fmt(listPrice(p).price)}</div>
                        {listPrice(p).originalPrice && <div className="text-[10px] text-ink-faint line-through">${fmt(listPrice(p).originalPrice!)}</div>}
                        {listPrice(p).caption && <div className="text-[10px] text-emerald-600 font-semibold mt-0.5 max-w-[170px] leading-tight">{listPrice(p).caption}</div>}
                      </td>
                      <td className="px-6 py-4">
                        {p.sizes.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                            {p.sizes.map(s => (
                              <span key={s.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-page-border text-ink-muted">
                                {s.size}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[10px] text-ink-faint">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-ink-muted bg-page-surface px-2.5 py-1.5 rounded-lg border border-page-border flex-shrink-0">
                            {p.images.length} {p.images.length === 1 ? 'foto' : 'fotos'}
                          </span>
                          {p.images.length === 0 && (
                            <button
                              onClick={() => navigate(`/products/${p.id}/edit?focus=images`)}
                              className="px-2.5 py-1.5 bg-brand-surface hover:bg-brand-primary border border-brand-border text-brand-primary hover:text-white rounded-xl transition-all flex items-center gap-1.5 text-xs font-bold shadow-sm"
                              title="Cargar fotos"
                            >
                              <Camera className="w-3.5 h-3.5" />
                              <span>Cargar imagen</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-lg border ${p.isActive
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                            : 'bg-rose-50 border-rose-200 text-rose-500'
                          }`}>
                          {p.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex flex-col items-stretch gap-2 w-28 ml-auto">
                          <button onClick={() => navigate(`/products/${p.id}/edit`)} className="px-3 py-1.5 text-sky-700 bg-sky-50 hover:text-white hover:bg-sky-600 rounded-xl transition-colors border border-sky-200 hover:border-sky-600 flex items-center justify-center gap-1.5 text-xs font-semibold whitespace-nowrap">
                            <Edit2 className="w-3.5 h-3.5" />
                            <span>Editar</span>
                          </button>
                          <button onClick={() => openDeleteConfirm(p.id)} className="px-3 py-1.5 text-rose-600 bg-rose-50 hover:text-white hover:bg-rose-600 rounded-xl transition-colors border border-rose-200 hover:border-rose-600 flex items-center justify-center gap-1.5 text-xs font-semibold whitespace-nowrap">
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Eliminar</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Spacer para la barra de paginación ── */}
      {totalPages > 1 && <div className="h-20" />}

      {/* Confirmación de eliminación */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar producto"
        message="¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer y borrará permanentemente el producto, junto con sus imágenes y talles de stock."
        confirmText="Eliminar permanentemente"
        cancelText="Cancelar"
        loading={deleting}
        type="danger"
      />

      {/* ── Barra de paginación fija ── */}
      {totalPages > 1 && (
        <div className="fixed bottom-14 lg:bottom-0 left-0 lg:left-64 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-page-border shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
            {/* Info */}
            <span className="text-[11px] text-ink-faint hidden sm:block">
              {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalProducts)} de {totalProducts}
            </span>

            {/* Páginas */}
            <div className="flex items-center gap-1 mx-auto sm:mx-0">
              <PageBtn
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Anterior"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </PageBtn>

              {getPageRange(currentPage, totalPages).map((p, i) =>
                p === null ? (
                  <span key={`ellipsis-${i}`} className="w-8 text-center text-xs text-ink-faint select-none">…</span>
                ) : (
                  <PageBtn
                    key={p}
                    onClick={() => goToPage(p)}
                    disabled={false}
                    active={p === currentPage}
                  >
                    {p}
                  </PageBtn>
                )
              )}

              <PageBtn
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Siguiente"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </PageBtn>
            </div>

            {/* Página actual en mobile */}
            <span className="text-[11px] text-ink-faint sm:hidden">
              {currentPage}/{totalPages}
            </span>

            {/* Spacer desktop */}
            <span className="hidden sm:block text-[11px] text-ink-faint">
              Página {currentPage} de {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const PageBtn: React.FC<{
  onClick: () => void;
  disabled: boolean;
  active?: boolean;
  children: React.ReactNode;
  'aria-label'?: string;
}> = ({ onClick, disabled, active, children, 'aria-label': ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all border ${active
        ? 'bg-brand-primary text-white border-brand-primary shadow-sm shadow-brand-glow'
        : disabled
          ? 'text-ink-faint border-transparent cursor-not-allowed opacity-40'
          : 'text-ink-muted border-page-border hover:border-brand-border hover:text-brand-primary hover:bg-brand-surface'
      }`}
  >
    {children}
  </button>
);
